'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { motion } from 'framer-motion';
import { History, Video, ChevronRight } from 'lucide-react';
import { Skeleton } from '@/shared/ui/skeleton';
import { Badge } from '@/shared/ui/badge';
import { EmptyState } from '@/shared/components/states/EmptyState';
import { ErrorState } from '@/shared/components/states/ErrorState';
import { SNAPPY } from '@/lib/motion';
import { relativeTime } from '@/lib/format';
import { CopyLinkIconButton } from '@/shared/components/CopyLinkButton';
import { AnalyticsLinkButton } from '@/features/analytics';
import { useMyRooms } from '@/features/rooms';
import type { MyRoom, RoomListStatus } from '@/features/rooms';

const STATUS_BADGE: Record<
  RoomListStatus,
  { label: string; variant: 'success' | 'soft-blue' | 'soft' }
> = {
  active: { label: 'Live', variant: 'success' },
  created: { label: 'Scheduled', variant: 'soft-blue' },
  ended: { label: 'Ended', variant: 'soft' },
  cancelled: { label: 'Cancelled', variant: 'soft' },
};

/** One contained, divided panel so the rows read as a single "recents" surface. */
function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-sm)]">
      {children}
    </div>
  );
}

/**
 * "Recent meetings" (§4.1) — turns a create-only screen into a lightweight home
 * base with zero new backend. On desktop it lives as a permanent side panel
 * (§3 ultrawide guidance); on mobile it stacks below the actions. Filtered
 * client-side to this salesperson. Every state handled: loading (skeleton in
 * the same panel so nothing shifts), error (inline, never a toast for a GET),
 * and empty.
 */
export function RecentMeetingsStrip({
  limit = 3,
  statusFilter,
  label = 'Recent',
  emptyTitle = 'No meetings yet',
  emptyDescription = 'Meetings you start or schedule will show up here.',
}: {
  limit?: number;
  /** Only include meetings whose status is in this list — e.g. `['created', 'active']`
   *  for an "Upcoming" tab, `['ended', 'cancelled']` for a "Done" tab. Omit to show everything. */
  statusFilter?: RoomListStatus[];
  label?: string;
  emptyTitle?: string;
  emptyDescription?: string;
}) {
  const router = useRouter();
  const { data, isPending, isError, error, refetch } = useMyRooms();

  const filtered = React.useMemo(() => {
    if (!data) return data;
    const byStatus = statusFilter ? data.filter((r) => statusFilter.includes(r.status)) : data;
    return limit > 0 ? byStatus.slice(0, limit) : byStatus;
  }, [data, statusFilter, limit]);

  const count = filtered?.length ?? 0;

  return (
    <section aria-label={label} className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
          <History className="h-3.5 w-3.5" aria-hidden="true" />
          {label}
        </p>
        {!isPending && !isError && count > 0 && (
          <span className="rounded-full bg-[var(--muted)] px-2 py-0.5 text-[0.6875rem] font-semibold tabular-nums text-[var(--text-muted)]">
            {count}
          </span>
        )}
      </div>

      {isPending ? (
        <Panel>
          <ul className="divide-y divide-[var(--border)]">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-center gap-3.5 px-4 py-3.5">
                <Skeleton className="h-10 w-10 shrink-0 rounded-[var(--radius-md)]" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-2/5" />
                  <Skeleton className="h-3 w-3/5" />
                </div>
                <Skeleton className="h-5 w-14 rounded-full" />
              </li>
            ))}
          </ul>
        </Panel>
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} className="py-6" />
      ) : count > 0 ? (
        <Panel>
          <ul className="divide-y divide-[var(--border)]">
            {filtered!.map((m: MyRoom) => {
              const badge = STATUS_BADGE[m.status];
              return (
                <li
                  key={m.id}
                  className="flex items-center gap-1 pr-2 transition-colors duration-150 hover:bg-[var(--surface-hover)]"
                >
                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.995 }}
                    transition={SNAPPY}
                    onClick={() => router.push(m.hostPath)}
                    className="group flex min-w-0 flex-1 items-center gap-3.5 px-4 py-3.5 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--focus-ring)]"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--primary-soft)] text-[var(--primary)]">
                      <Video className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-[var(--text-primary)]">
                        {m.guestName || m.topic}
                      </p>
                      <p className="truncate text-xs text-[var(--text-muted)]">
                        {m.createdAt ? relativeTime(new Date(m.createdAt)) : ''}
                      </p>
                    </div>
                    {badge && (
                      <Badge variant={badge.variant} size="sm">
                        {badge.label}
                      </Badge>
                    )}
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-[var(--text-muted)] opacity-40 transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100 motion-reduce:transition-none"
                      aria-hidden="true"
                    />
                  </motion.button>
                  {/* Per-row analytics entry point (outcome, attendance,
                      recording) — same round icon-action pattern as copy-link. */}
                  <AnalyticsLinkButton
                    meetingId={m.id}
                    leadName={m.guestName || m.topic}
                    href={`/r/${m.id}/analytics`}
                  />
                  {/* Grab the guest link without opening the meeting — the most
                      common reason to revisit a recent row. */}
                  <CopyLinkIconButton
                    url={m.joinUrl}
                    label={`Copy guest link for ${m.guestName || m.topic}`}
                    successMessage="Guest link copied"
                  />
                </li>
              );
            })}
          </ul>
        </Panel>
      ) : (
        <EmptyState
          icon={<History className="h-5 w-5" aria-hidden="true" />}
          title={emptyTitle}
          description={emptyDescription}
          className="py-10"
        />
      )}
    </section>
  );
}
